import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Reels reais de eventos publicados no Instagram @stibpalestrasilenciosa.
 * Para incluir um reel novo: acrescente uma linha em REELS (código do link
 * instagram.com/reel/CODIGO/, categoria e legenda curta). Os mais novos vão no topo.
 */
type Cat = 'congressos' | 'bem-estar';
type Reel = { code: string; cat: Cat; titulo: string };

const REELS: Reel[] = [
  { code: 'DewQHjOCSv5', cat: 'congressos', titulo: '81ª SOEA (Aracaju/SE): mesa de som e transmissão em cada auditório' },
  { code: 'DetrVvKCWVU', cat: 'congressos', titulo: '81ª SOEA: Arena Conexões lotada, zero caixa de som' },
  { code: 'DerGfmjCRxj', cat: 'congressos', titulo: '81ª SOEA: 2.000 fones conferidos antes da plateia chegar' },
  { code: 'DejYG1NiV3z', cat: 'congressos', titulo: '81ª SOEA: 7 palestras ao mesmo tempo, cada plateia no seu canal' },
  { code: 'Dehmz7gCCBT', cat: 'congressos', titulo: '81ª SOEA: cabine, intérpretes e fones montados pela STIB' },
  { code: 'DehMd2glHpl', cat: 'congressos', titulo: '81ª SOEA, 2º dia: 2.000 fones e 7 palestras simultâneas' },
  { code: 'DehIZkWEuKM', cat: 'congressos', titulo: 'Congresso Brasileiro de Pediatria: plateia no fone' },
  { code: 'Defg1XblYAq', cat: 'congressos', titulo: 'Congresso Brasileiro de Pediatria: estande lotado, todos no fone' },
  { code: 'DefWqx-FaAo', cat: 'congressos', titulo: '81ª SOEA: Stand Crea com palestras no fone' },
  { code: 'DefWXqnCOGL', cat: 'congressos', titulo: 'Palestra silenciosa em grande escala com fones LED' },
  { code: 'DefWBRnjcTu', cat: 'congressos', titulo: 'Um fone em cada cadeira: salão pronto e testado' },
  { code: 'DefVnAUDe1L', cat: 'congressos', titulo: '81ª SOEA: 7 espaços, cada plateia ouvindo só a sua palestra' },
  { code: 'DeelnqpuP3B', cat: 'congressos', titulo: 'Congresso Brasileiro de Pediatria (Expominas): palestras no estande' },
  { code: 'DeekCnKuOWS', cat: 'congressos', titulo: 'CONAHP 2026 (Transamerica Expo): mais de 3.000 participantes' },
  { code: 'DecubTcluHS', cat: 'bem-estar', titulo: 'Aula silenciosa de relaxamento com fones sem fio' },
  { code: 'DeZgeOfjWCw', cat: 'bem-estar', titulo: 'Aula ao ar livre com fones sem fio' },
  { code: 'DeXk26lj3Eo', cat: 'bem-estar', titulo: 'Aula funcional com fones sem fio na Arena Soul' },
  { code: 'DeRyG6ZDTHJ', cat: 'bem-estar', titulo: 'Relaxamento guiado com fones sem fio' },
  { code: 'DePNNtFgUUL', cat: 'bem-estar', titulo: 'Meditação guiada com fones sem fio' },
  { code: 'DeMohsPjQO-', cat: 'bem-estar', titulo: 'Sunset reset: aula com fones sem fio' },
  { code: 'DeKDr15D7SS', cat: 'bem-estar', titulo: 'Experiência imersiva com fones sem fio' },
  { code: 'DeHe163DV06', cat: 'bem-estar', titulo: 'Aula em grupo com fones sem fio' },
  { code: 'DeFjNnPlDtW', cat: 'bem-estar', titulo: 'Salão amplo, uma única voz conduzindo, nenhuma caixa de som' },
  { code: 'DeDFT4zCDF4', cat: 'bem-estar', titulo: 'Le Sensier no Parque Ibirapuera (SP): degustação com fones' },
  { code: 'DeAgm0RAYwa', cat: 'bem-estar', titulo: 'Aula silenciosa e meditação no Parque da Cidade (Brasília/DF)' },
  { code: 'DbVpgWtuP1x', cat: 'congressos', titulo: 'ENAVSEG 2026: três palestras simultâneas, escolha no fone' },
  { code: 'DaOP3c_A4zY', cat: 'congressos', titulo: 'ENAVSEG 2026: entrega de fones na palestra silenciosa' },
];

const FILTROS: { id: 'todos' | Cat; label: string }[] = [
  { id: 'todos', label: 'Todos' },
  { id: 'congressos', label: 'Congressos, feiras e estandes' },
  { id: 'bem-estar', label: 'Aulas, yoga e bem-estar' },
];

const INICIAL = 9;
const IG = 'https://www.instagram.com/stibpalestrasilenciosa/';

export default function InstagramReels() {
  const [filtro, setFiltro] = useState<'todos' | Cat>('todos');
  const [mostrar, setMostrar] = useState(INICIAL);
  const frames = useRef<Map<string, HTMLIFrameElement>>(new Map());

  const lista = useMemo(() => REELS.filter((r) => filtro === 'todos' || r.cat === filtro), [filtro]);
  const visiveis = lista.slice(0, mostrar);

  // O embed do Instagram informa a própria altura via postMessage; ajustamos cada iframe.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!/instagram\.com$/.test(new URL(e.origin).hostname)) return;
      let d: any = e.data;
      if (typeof d === 'string') { try { d = JSON.parse(d); } catch { return; } }
      if (!d || d.type !== 'MEASURE' || !d.details?.height) return;
      frames.current.forEach((f) => {
        if (f.contentWindow === e.source) f.style.height = `${d.details.height}px`;
      });
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <section id="reels" className="py-24 bg-gray-50 dark:bg-gray-800/50 scroll-mt-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3">Eventos reais em vídeo</p>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">Nossos eventos no Instagram</h2>
          <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Reels gravados nos eventos que atendemos: congressos, feiras, estandes, palestras simultâneas e aulas silenciosas.
            Tudo com os fones sem fio e a equipe técnica da STIB.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {FILTROS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { setFiltro(f.id); setMostrar(INICIAL); }}
                className={`font-body text-sm font-semibold px-5 py-2.5 rounded-full border transition-colors ${filtro === f.id ? 'bg-primary text-secondary border-primary' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          {visiveis.map((r) => (
            <figure key={r.code} className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
              <iframe
                ref={(el) => { if (el) frames.current.set(r.code, el); else frames.current.delete(r.code); }}
                src={`https://www.instagram.com/reel/${r.code}/embed/`}
                title={r.titulo}
                loading="lazy"
                allow="encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                scrolling="no"
                className="w-full block border-0 bg-white"
                style={{ height: 620 }}
              />
              <figcaption className="font-body text-sm text-gray-700 dark:text-gray-300 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                {r.titulo}
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
          {mostrar < lista.length && (
            <button
              type="button"
              onClick={() => setMostrar((m) => m + INICIAL)}
              className="font-body font-semibold bg-secondary text-white px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity"
            >
              Ver mais vídeos ({lista.length - mostrar})
            </button>
          )}
          <a
            href={IG}
            target="_blank"
            rel="noopener noreferrer"
            className="font-body font-semibold border border-secondary dark:border-white text-secondary dark:text-white px-7 py-3.5 rounded-full hover:bg-secondary hover:text-white transition-colors"
          >
            Seguir @stibpalestrasilenciosa
          </a>
          <Link to="/eventos" className="font-body font-semibold text-primary hover:underline">Ver fotos de eventos reais</Link>
        </div>
      </div>
    </section>
  );
}
